/**
 * ChatScreen - Plugin chat entry point
 *
 * Rendered at /chat inside PowerPoint's add-in webview.
 * Wraps AIChat in Office.js context.
 *
 * Auth flow: SSO first (Microsoft approved the SSO update)
 *  1. On mount, silent SSO via OfficeRuntime.auth.getAccessToken using the
 *     Microsoft account already signed into Office. Backend exchanges the
 *     token for a Firebase custom token. No UI when it works.
 *  2. If silent SSO cannot complete, the sign-in panel shows:
 *     PRIMARY "Continue with Microsoft 365" (SSO, with the dialog account
 *     picker as automatic fallback) and SECONDARY "Sign in with a different
 *     Microsoft account" (Office Dialog API account picker directly).
 *  3. Office web has no SSO support, so it keeps the in-pane Microsoft
 *     popup plus the email/password form.
 *
 * Layout:
 *   OfficeProvider → UIProvider → AIChat
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography, TextField, Divider, Link, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { signInWithCredential, signInWithCustomToken, GoogleAuthProvider, OAuthProvider, OAuthCredential, onAuthStateChanged } from 'firebase/auth';
import { auth, signInWithMicrosoft, signInWithEmail, signUpWithEmail, resetPassword } from '../../shared/services/firebase';
import { OfficeProvider, useOffice } from '../../shared/services/office/OfficeContext';
import { openAuthDialog, trySsoSignIn } from '../../shared/services/office/OfficeAuth';
import { getAppCopy, getAppName } from './appCopy';
import { UIProvider } from '../../shared/contexts/UIContext';
import { useUser, useAuth } from '../../shared/contexts/UserContext';
import AIChat from './ai-chat/AIChat';
import { brandConfig } from '../../shared/design/theme';

/* global Office */

/**
 * True when the task pane is running under Office on the web. In that host the
 * Office Dialog API's messageParent channel back to the task pane does not
 * deliver reliably, so we authenticate DIRECTLY in the task pane with Firebase
 * signInWithPopup (the same approach the public website uses) instead of opening
 * the dialog. Desktop Office keeps the dialog flow. Office.context.platform is
 * reliable in the task pane (unlike inside a dialog window).
 */
function isOfficeWebHost() {
  try {
    return typeof Office !== 'undefined'
      && !!Office.context
      && Office.context.platform === Office.PlatformType.OfficeOnline;
  } catch (e) {
    return false;
  }
}

/**
 * Inner component that renders once Office.js is ready.
 */
function ChatInner() {
  const { isOfficeReady, isInOffice, app } = useOffice();
  const { isAuthenticated, authLoading, currentUser } = useUser();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [authStatus, setAuthStatus] = useState('idle'); // idle | waiting | error
  const [authError, setAuthError] = useState(null);

  // In-pane auth (Office web only). On the web host we sign in directly in the
  // task pane instead of via the Office Dialog API.
  const officeWeb = isOfficeWebHost();
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [emailMode, setEmailMode] = useState('signin'); // signin | signup | reset
  const [emailBusy, setEmailBusy] = useState(false);

  // Microsoft sign-in directly in the task pane (Office web). signInWithPopup
  // keeps the task pane in place and opens Microsoft in a child popup; on success
  // UserContext's onAuthStateChanged flips the UI. No dialog, no messageParent.
  const handleMicrosoftWeb = useCallback(async () => {
    try {
      setAuthError(null);
      setAuthStatus('waiting');
      await signInWithMicrosoft();
      // isAuthenticated flips via UserContext's auth listener.
    } catch (err) {
      console.error('[ChatScreen] In-pane Microsoft sign-in failed:', err);
      setAuthError(err.message || 'Microsoft sign-in failed. Please try again.');
      setAuthStatus('error');
    }
  }, []);

  // Email/password sign-in directly in the task pane (Office web).
  const handleEmailWeb = useCallback(async (e) => {
    e?.preventDefault();
    if (emailBusy) return;
    setAuthError(null);
    setEmailBusy(true);
    try {
      if (emailMode === 'reset') {
        await resetPassword(emailInput.trim());
        setEmailMode('signin');
        setAuthError('Reset link sent. Check your email.');
      } else if (emailMode === 'signup') {
        await signUpWithEmail(emailInput.trim(), passwordInput, nameInput.trim() || null);
        // signed in immediately; UserContext picks it up.
      } else {
        await signInWithEmail(emailInput.trim(), passwordInput);
      }
    } catch (err) {
      setAuthError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setEmailBusy(false);
    }
  }, [emailBusy, emailMode, emailInput, passwordInput, nameInput]);

  // Reset auth UI when user signs out
  useEffect(() => {
    if (!isAuthenticated) {
      setAuthStatus('idle');
      setAuthError(null);
    }
  }, [isAuthenticated]);

  // Silent SSO attempt on plugin mount. If the user is already signed
  // into PowerPoint with a Microsoft 365 account, this completes Firebase
  // auth without any UI. If SSO is unavailable (Google user, older Office,
  // tenant blocked, etc.), we silently fall through to the Dialog-based
  // sign-in UI below — the user sees the normal sign-in screen and nothing
  // looks broken.
  //
  // Feature flag: REACT_APP_OFFICE_SSO_ENABLED. Defaults to ON in
  // production once REACT_APP_OFFICE_SSO_ENABLED is set to "true" in the
  // build env. Without that env var the SSO path stays dormant so an
  // incomplete Azure AD setup doesn't block sign-in for everyone.
  // The "already tried SSO" guard is a REF, not state, on purpose.
  //
  // It was previously useState + listed in this effect's dependency array.
  // Because the effect ALSO called setSsoTried(true) on entry, that state
  // change tore the effect down (running the cleanup → cancelled=true) and
  // re-ran it mid-flight. For fast-failing SSO the async work resolved
  // before teardown, so the fallback still logged; but for a slow/SUCCESSFUL
  // exchange (token + network round-trip), teardown won first — so when the
  // work finally resolved, `if (cancelled) return` discarded it silently and
  // the `finally` skipped clearing ssoChecking. Net effect: the exact
  // symptom we saw — "exchange responded 200" then nothing, spinner stuck.
  //
  // A ref doesn't trigger re-renders, so flipping it can't re-run the effect.
  const ssoTriedRef = useRef(false);
  // `ssoChecking` is a DEDICATED silent-attempt state, separate from the
  // dialog's `authStatus === 'waiting'`.
  const [ssoChecking, setSsoChecking] = useState(false);

  // Shared SSO routine used by BOTH the silent auto-trigger and the explicit
  // "Continue with Microsoft 365" button. `silent` suppresses error surfacing
  // for the automatic path (failures there just fall through to the buttons).
  // Returns true if SSO signed the user in, false otherwise. Callers decide what
  // to do on false: the auto path falls through to the sign-in UI silently; the
  // manual button falls back to the host's reliable sign-in (popup/dialog)
  // instead of dead-ending with an error. NOTE: SSO is genuinely unsupported in
  // some hosts (e.g. Office on the web returns code 13000) — that's expected, not
  // a user-facing failure, so this never surfaces an error by itself.
  const runSso = useCallback(async () => {
    setSsoChecking(true);
    try {
      const result = await trySsoSignIn();
      if (result.ok) {
        await Promise.race([
          signInWithCustomToken(auth, result.customToken),
          new Promise((_, reject) => setTimeout(() => reject(new Error('signInWithCustomToken timed out')), 15000)),
        ]);
        console.log('[ChatScreen] Auth completed via Office SSO', { uid: result.uid });
        return true;
      }
      console.log('[ChatScreen] SSO unavailable:', result.reason);
      return false;
    } catch (err) {
      console.warn('[ChatScreen] SSO attempt threw:', err);
      return false;
    } finally {
      setSsoChecking(false);
    }
  }, []);

  useEffect(() => {
    if (ssoTriedRef.current) return;
    if (!isOfficeReady || !isInOffice) return;
    if (isAuthenticated || authLoading) return;
    if (process.env.REACT_APP_OFFICE_SSO_ENABLED !== 'true') {
      ssoTriedRef.current = true;
      return;
    }
    // Session-scoped suppression: when the user explicitly signs out we set
    // this flag so the auto-SSO does NOT immediately sign them back into the
    // same Office account (they may want a different one). sessionStorage
    // clears when the task pane closes, so auto-SSO is naturally re-enabled
    // next session.
    try {
      if (sessionStorage.getItem('restack_sso_suppressed') === '1') {
        ssoTriedRef.current = true;
        return;
      }
    } catch (e) { /* sessionStorage unavailable — proceed with auto-SSO */ }
    ssoTriedRef.current = true;

    let cancelled = false;
    (async () => { if (!cancelled) await runSso(); })();
    return () => { cancelled = true; };
  }, [isOfficeReady, isInOffice, isAuthenticated, authLoading, runSso]);

  // Office Dialog API auth flow
  const handleSignIn = async () => {
    try {
      setAuthError(null);
      setAuthStatus('waiting');

      const origin = window.location.origin;
      // Forward the host app so the dialog/tab copy reads "...inside Excel/Word"
      // (the dialog window can't read clientApp on its own).
      const authUrl = `${origin}/chat/auth?app=${encodeURIComponent(app || 'powerpoint')}`;

      if (isInOffice) {
        // Use Office Dialog API — opens a proper browser window
        const data = await openAuthDialog({ authUrl, height: 60, width: 40 });
        console.log('[ChatScreen] Auth dialog returned:', data);

        if (data.bridgeCustomToken) {
          // Preferred path for ALL providers (Google, Microsoft, email/password).
          // The dialog signed in via signInWithPopup, then asked the backend to
          // mint a Firebase custom token bound to the same uid. signInWithCustomToken
          // is provider-agnostic — works regardless of webview storage isolation
          // and avoids Microsoft's single-use id_token problem entirely.
          await signInWithCustomToken(auth, data.bridgeCustomToken);
          console.log('[ChatScreen] Auth completed via Office Dialog (bridge custom token)');
        } else if (data.googleIdToken) {
          // Legacy Google credential path. Kept as a fallback for stale
          // dialog builds that don't yet mint a bridge token. New deploys
          // always include `bridgeCustomToken` and skip this branch.
          const credential = GoogleAuthProvider.credential(data.googleIdToken, data.googleAccessToken || null);
          await signInWithCredential(auth, credential);
          console.log('[ChatScreen] Auth completed via Office Dialog (Google credential — legacy)');
        } else if (data.microsoftCredentialJSON || data.microsoftIdToken) {
          // Microsoft / Azure AD path is fundamentally different from Google.
          // Microsoft id_tokens can be exchanged with Firebase's identitytoolkit
          // endpoint exactly ONCE — and the dialog's signInWithPopup already
          // consumed that exchange. Calling signInWithCredential here with the
          // same token always throws auth/invalid-credential-or-provider-id,
          // even with rawNonce preserved through OAuthCredential.toJSON().
          //
          // We don't need to re-sign-in anyway: the dialog already authenticated
          // the user via signInWithPopup, and Firebase Auth persists to IndexedDB
          // which Office shares across the dialog + task pane webviews (same
          // origin → re-stack.com). The parent's onAuthStateChanged fires
          // automatically once persistence propagates — usually within ~500ms.
          // We just wait for that side channel to settle. Fall back to a
          // credential exchange only if shared persistence somehow didn't work
          // (defensive — shouldn't happen in same-origin Office dialogs).
          console.log('[ChatScreen] Microsoft sign-in completed in dialog — waiting for auth state to propagate');
          await new Promise((resolve) => {
            if (auth.currentUser) return resolve();
            let unsubscribe = () => {};
            const timeout = setTimeout(() => { unsubscribe(); resolve(); }, 3000);
            unsubscribe = onAuthStateChanged(auth, (user) => {
              if (user) {
                clearTimeout(timeout);
                unsubscribe();
                resolve();
              }
            });
          });
          if (!auth.currentUser) {
            // Shared-persistence side channel didn't fire within 3s. Try the
            // credential exchange as a last resort. This will most likely throw
            // for the reasons above, but if it does work in some environment
            // we don't anticipate, great.
            console.warn('[ChatScreen] Shared persistence did not propagate; attempting credential fallback');
            let credential = null;
            if (data.microsoftCredentialJSON) {
              credential = OAuthCredential.fromJSON(data.microsoftCredentialJSON);
            }
            if (!credential && data.microsoftIdToken) {
              const msProvider = new OAuthProvider('microsoft.com');
              credential = msProvider.credential({
                idToken: data.microsoftIdToken,
                accessToken: data.microsoftAccessToken || null
              });
            }
            if (!credential) {
              throw new Error('Microsoft sign-in returned an unrecognized credential payload.');
            }
            await signInWithCredential(auth, credential);
          }
          console.log('[ChatScreen] Auth completed via Office Dialog (Microsoft)');
        } else if (data.token) {
          // Email/password fallback (reached only if the dialog's bridge-token
          // mint failed; the happy path is the data.bridgeCustomToken branch
          // above). The dialog already signed the user into Firebase, and that
          // session lives in shared (same-origin) IndexedDB — so wait for the
          // parent's onAuthStateChanged to pick it up, exactly like the
          // Microsoft branch. Previously this threw "not supported," which is
          // why email sign-in required a manual refresh to take effect.
          console.log('[ChatScreen] Email sign-in completed in dialog — waiting for auth state to propagate');
          await new Promise((resolve) => {
            if (auth.currentUser) return resolve();
            let unsubscribe = () => {};
            const timeout = setTimeout(() => { unsubscribe(); resolve(); }, 4000);
            unsubscribe = onAuthStateChanged(auth, (user) => {
              if (user) { clearTimeout(timeout); unsubscribe(); resolve(); }
            });
          });
          if (!auth.currentUser) {
            // The dialog's session lives in shared (same-origin) storage but
            // didn't propagate to this task-pane view in time. Reload the
            // pane to pick it up — this automates the manual refresh that
            // was previously needed for the email path.
            console.log('[ChatScreen] Email auth not yet propagated — reloading task pane to pick up session');
            window.location.reload();
            return;
          }
          console.log('[ChatScreen] Auth completed via Office Dialog (email/password)');
        }
      } else {
        // Fallback for browser testing — open in new tab
        window.open(authUrl, '_blank');
        setAuthError('Complete sign-in in the opened tab, then refresh this page.');
        setAuthStatus('error');
        return;
      }
    } catch (err) {
      console.error('[ChatScreen] Auth flow failed:', err);
      setAuthError(err.message || 'Sign-in failed. Please try again.');
      setAuthStatus('error');
    }
  };

  // Loading state
  if (!isOfficeReady || authLoading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 2,
      }}>
        <CircularProgress size={24} />
        <Typography variant="caption" color="text.secondary">
          {!isOfficeReady ? `Connecting to ${getAppName(app)}...` : 'Loading...'}
        </Typography>
      </Box>
    );
  }

  // Unauthenticated state — prompt sign-in
  const accentWarm = theme.palette.accentBubble?.warm || '#D4694E';

  if (!isAuthenticated) {
    const cardStyle = {
      width: '100%',
      maxWidth: 300,
      borderRadius: theme.spacing(1.5),
      border: `1px solid ${alpha(accentWarm, 0.35)}`,
      backgroundColor: alpha(accentWarm, 0.04),
      boxShadow: `0 2px 8px ${alpha(accentWarm, 0.08)}`,
    };

    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        px: 3,
        textAlign: 'center',
        bgcolor: 'background.default',
      }}>
        {/* Logo */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
            backgroundColor: alpha(accentWarm, 0.1),
            border: `1px solid ${alpha(accentWarm, 0.15)}`,
          }}
        >
          <brandConfig.icon sx={{ fontSize: 28, color: accentWarm }} />
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: theme.typography.pxToRem(17),
            color: theme.palette.text.primary,
            letterSpacing: '-0.015em',
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          Sign in to Restack
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2.5 }}>
          {getAppCopy(app).tagline}
        </Typography>

        {/* Silent SSO attempt in progress — neutral message (no browser tab
            involved). Shown only while ssoChecking; always clears. */}
        {ssoChecking && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} sx={{ color: accentWarm }} />
            <Typography variant="caption" color="text.secondary">
              Signing you in...
            </Typography>
          </Box>
        )}

        {/* Dialog flow waiting state — only when NOT doing the silent SSO
            check, so the "complete in your browser" copy can't leak into
            the SSO path. */}
        {authStatus === 'waiting' && !ssoChecking && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} sx={{ color: accentWarm }} />
            <Typography variant="caption" color="text.secondary">
              Complete sign-in in your browser...
            </Typography>
          </Box>
        )}

        {authError && (
          <Typography
            variant="caption"
            sx={{
              maxWidth: 280,
              mb: 1.5,
              color: authError.includes('Reset link sent') ? 'success.main' : 'error.main',
            }}
          >
            {authError}
          </Typography>
        )}

        {authStatus !== 'waiting' && !ssoChecking && (
          <Box sx={{ width: '100%', maxWidth: 300, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {/* PRIMARY: SSO with the Microsoft account already signed into
                Office. Microsoft approved the SSO update, so this is now the
                lead path on desktop. If SSO is unavailable on this host, the
                click falls back to the dialog account picker automatically,
                so the button never dead-ends. On Office web (no SSO support)
                the primary stays the in-pane Microsoft popup. */}
            <Box
              component="button"
              onClick={officeWeb ? handleMicrosoftWeb : async () => {
                setAuthError(null);
                const ok = await runSso();
                if (!ok) handleSignIn();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.2,
                width: '100%',
                px: 3,
                py: 1.25,
                ...cardStyle,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.primary,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(accentWarm, 0.08),
                  borderColor: alpha(accentWarm, 0.5),
                },
              }}
            >
              <svg width="14" height="14" viewBox="0 0 23 23">
                <path fill="#f25022" d="M1 1h10v10H1z"/>
                <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                <path fill="#7fba00" d="M12 1h10v10H12z"/>
                <path fill="#ffb900" d="M12 12h10v10H12z"/>
              </svg>
              {officeWeb ? 'Sign in with Microsoft' : 'Continue with Microsoft 365'}
            </Box>

            {officeWeb ? (
              /* OFFICE WEB: authenticate directly in the task pane. The Office
                 Dialog API's messageParent hand-back does not work on the web
                 host, so we use Firebase signInWithPopup (Microsoft, above) and
                 an inline email/password form (below) — the same approach as the
                 public website. */
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 0.5 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">or use email</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
                <Box component="form" onSubmit={handleEmailWeb} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {emailMode === 'signup' && (
                    <TextField size="small" placeholder="Full name" value={nameInput} onChange={(e) => setNameInput(e.target.value)} fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
                  )}
                  <TextField size="small" type="email" placeholder="Email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} required fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
                  {emailMode !== 'reset' && (
                    <TextField size="small" type="password" placeholder="Password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} required fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
                  )}
                  <Button type="submit" variant="contained" disabled={emailBusy} fullWidth
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', py: 1, boxShadow: 'none' }}>
                    {emailBusy ? <CircularProgress size={18} color="inherit" /> :
                      emailMode === 'reset' ? 'Send reset link' :
                      emailMode === 'signup' ? 'Create account' : 'Sign in'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
                  {emailMode === 'signin' && (
                    <>
                      <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { setEmailMode('signup'); setAuthError(null); }}>Create account</Link>
                      <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { setEmailMode('reset'); setAuthError(null); }}>Forgot password?</Link>
                    </>
                  )}
                  {(emailMode === 'signup' || emailMode === 'reset') && (
                    <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { setEmailMode('signin'); setAuthError(null); }}>Back to sign in</Link>
                  )}
                </Box>
              </>
            ) : (
              /* DESKTOP secondary: manual Microsoft sign-in via the Office
                 dialog account picker. For users whose Office account is not
                 the one they want, or when SSO cannot complete. */
              <Box
                component="button"
                onClick={() => {
                  setAuthError(null);
                  handleSignIn();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  px: 3,
                  py: 0.75,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.78rem',
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.text.secondary,
                  transition: 'color 0.2s ease',
                  '&:hover': { color: theme.palette.text.primary },
                }}
              >
                Sign in with a different Microsoft account
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Email verification gate removed — see UserContext (needsEmailVerification
  // is now always false). Email/password users land straight in the app.

  // Work-email gating removed: with Office SSO any Microsoft-authenticated
  // user is allowed, and free trial credits no longer exist, so there's no
  // abuse vector that the personal-email block was guarding against.

  // Authenticated — render the AI chat (no presentation context needed)
  return (
    <UIProvider>
      <Box sx={{
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <AIChat
          collapsed={false}
          width="100%"
          position="right"
          readOnly={false}
        />
      </Box>
    </UIProvider>
  );
}

/**
 * ChatScreen wraps everything in the OfficeProvider.
 */
export default function ChatScreen() {
  return (
    <OfficeProvider>
      <ChatInner />
    </OfficeProvider>
  );
}
